import { useEffect, useMemo, useRef } from 'react';
import ProposalPreview from './preview-proposal/proposal-preview';
import { useDispatch } from 'react-redux';
import { clearUpdateTypeModal } from '../../../../features/gms-state-and-modal/modal-slice';
import { useProposalData } from '../../../../hooks/populate-data/use-proposal-data';
import CloseButtonModal from '../../../ui/close-button-modal';
import LoadingState from '../../../ui/loadibg-state';
import { useProposalTypeData } from '../../../../hooks/proposal/use-proposal-type-data';
import { clearApproveRejectConflictProposal } from '../../../../features/conflict-proposal/conflict-proposal-slice';

export default function ProposalDetails() {

    const dispatch = useDispatch();
    const { proposal, isLoadingProposal, repopulateAll, } = useProposalData();
    const { groupName, natureOfBusinessItem } = useProposalTypeData();

    const populatedProposalRef = useRef<string | null>(null);
    
    /*
    |--------------------------------------------------------------------------
    | Create a signature of the API data
    |--------------------------------------------------------------------------
    |
    | The application_id stays the same when another user updates the
    | proposal, so we compare the actual API response instead.
    |
    |--------------------------------------------------------------------------
    */
    const proposalSignature = useMemo(
        () => (proposal ? JSON.stringify(proposal) : null),
        [proposal],
    );

    useEffect(() => {
        if (!proposal?.application_id || !proposalSignature) {
            return;
        }

        if (populatedProposalRef.current === proposalSignature) {
            return;
        }

        populatedProposalRef.current = proposalSignature;

        repopulateAll();
    }, [ proposal?.application_id, proposalSignature, repopulateAll,  ]);

    const handleCloseModal = () => {
        dispatch(clearUpdateTypeModal());
        dispatch(clearApproveRejectConflictProposal())
    };

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4">
            <div className="my-auto flex h-[96dvh] w-full flex-col overflow-hidden rounded bg-[#f5f9fd] shadow sm:h-[90dvh] sm:max-w-[95vw] lg:max-w-[700px] xl:max-w-[1000px] 2xl:max-w-[1100px]">
                {isLoadingProposal ? (
                    <LoadingState />
                ) : (
                    <>
                        {/* MODAL HEADER */}
                        <div className="flex items-center justify-between border-b border-gray-200 bg-[#f8fafc] px-5 py-3 sm:px-6">
                            <div className="min-w-0 flex-1">
                                <div className="flex min-w-0 items-center gap-2">
                                    <div className="h-2 w-2 shrink-0 rounded-full bg-[#186cb2]" />
                                    <p className="truncate text-sm font-semibold text-gray-800">
                                        {groupName ?? '-'}
                                    </p>
                                </div>
                                <p className="mt-0.5 truncate pl-4 text-[11px] text-gray-500">
                                    {natureOfBusinessItem?.name ?? 'No nature of business specified'}
                                </p>
                            </div>
                            <div className="ml-3 flex shrink-0 items-center">
                                <CloseButtonModal handleClose={handleCloseModal} />
                            </div>
                        </div>
                        {/* SCROLLABLE CONTENT */}
                        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-5 sm:py-4 lg:px-6">
                            <div className="space-y-3">
                                <ProposalPreview />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}